/**
 * Shop The Look / Curated Outfit Section Script
 * Handles:
 * - Two-way Hotspot Pin <-> Garment Card interaction with smooth scrolling & highlighting
 * - Interactive Color Swatches and Size Pills variant switching
 * - Live Price, Compare Price, and Variant Image updates
 * - Dynamic Live Bundle total calculation and savings display
 * - Single item Add to Bag with instant micro-animation feedback
 * - Complete Look / Bundle Add to Bag (all outfit pieces simultaneously)
 * - Automatic Slide-Open of Minimog Cart Drawer on add to bag
 * - Native Minimog theme events, live cart re-rendering, and pubSubEvents
 */

if (!customElements.get('m-shop-the-look')) {
  class MShopTheLook extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.initPins();
      this.initOptions();
      this.initSingleAtc();
      this.initBundleAtc();
      this.updateBundleSummary();
    }

    initPins() {
      const pins = this.querySelectorAll('.m-shop-the-look__pin');
      const items = this.querySelectorAll('.m-shop-the-look__item');

      pins.forEach((pin) => {
        pin.addEventListener('click', (e) => {
          e.preventDefault();
          const targetIndex = pin.dataset.pinIndex;
          this.activateItem(targetIndex);
        });
      });

      items.forEach((item) => {
        item.addEventListener('mouseenter', () => {
          const itemIndex = item.dataset.itemIndex;
          this.highlightPin(itemIndex);
        });
        item.addEventListener('mouseleave', () => {
          this.clearPinHighlights();
        });
      });
    }

    activateItem(index) {
      const pins = this.querySelectorAll('.m-shop-the-look__pin');
      const items = this.querySelectorAll('.m-shop-the-look__item');

      pins.forEach((p) => p.classList.remove('is-active'));
      const activePin = this.querySelector(`.m-shop-the-look__pin[data-pin-index="${index}"]`);
      if (activePin) activePin.classList.add('is-active');

      items.forEach((item) => item.classList.remove('is-highlighted'));
      const targetItem = this.querySelector(`.m-shop-the-look__item[data-item-index="${index}"]`);
      if (targetItem) {
        targetItem.classList.add('is-highlighted');
        targetItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

        setTimeout(() => {
          targetItem.classList.remove('is-highlighted');
        }, 2500);
      }
    }

    highlightPin(index) {
      const pin = this.querySelector(`.m-shop-the-look__pin[data-pin-index="${index}"]`);
      if (pin) pin.classList.add('is-active');
    }

    clearPinHighlights() {
      const pins = this.querySelectorAll('.m-shop-the-look__pin');
      pins.forEach((p) => p.classList.remove('is-active'));
    }

    initOptions() {
      const items = this.querySelectorAll('.m-shop-the-look__item');

      items.forEach((item) => {
        const optionBtns = item.querySelectorAll('.m-shop-the-look__swatch, .m-shop-the-look__pill');
        const jsonEl = item.querySelector('.m-stl-variants-json');
        if (!jsonEl) return;

        let variants = [];
        try {
          variants = JSON.parse(jsonEl.textContent);
        } catch (err) {
          console.error('Error parsing variants JSON:', err);
          return;
        }

        optionBtns.forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            const optionPosition = btn.dataset.optionPosition;
            const optionValue = btn.dataset.optionValue;
            const optionRow = btn.closest('.m-shop-the-look__option-row');

            if (optionRow) {
              // Update selected class on buttons in this row
              const siblingBtns = optionRow.querySelectorAll('.m-shop-the-look__swatch, .m-shop-the-look__pill');
              siblingBtns.forEach((b) => b.classList.remove('is-selected'));
              btn.classList.add('is-selected');

              // Update option value label in header
              const labelEl = optionRow.querySelector('[data-option-val-label]');
              if (labelEl) labelEl.textContent = optionValue;
            }

            // Read selected values across all options for this item
            const opt1Btn = item.querySelector('.m-shop-the-look__option-row[data-option-position="1"] .is-selected');
            const opt2Btn = item.querySelector('.m-shop-the-look__option-row[data-option-position="2"] .is-selected');
            const opt3Btn = item.querySelector('.m-shop-the-look__option-row[data-option-position="3"] .is-selected');

            const selectedOpt1 = opt1Btn ? opt1Btn.dataset.optionValue : null;
            const selectedOpt2 = opt2Btn ? opt2Btn.dataset.optionValue : null;
            const selectedOpt3 = opt3Btn ? opt3Btn.dataset.optionValue : null;

            // Find matching variant
            const matchingVariant = variants.find((v) => {
              const match1 = !selectedOpt1 || v.option1 === selectedOpt1;
              const match2 = !selectedOpt2 || v.option2 === selectedOpt2;
              const match3 = !selectedOpt3 || v.option3 === selectedOpt3;
              return match1 && match2 && match3;
            });

            if (matchingVariant) {
              item.dataset.variantId = matchingVariant.id;
              item.dataset.variantPrice = matchingVariant.price;
              item.dataset.variantComparePrice = matchingVariant.compare_at_price || matchingVariant.price;
              item.dataset.variantAvailable = matchingVariant.available ? 'true' : 'false';

              // Update price display
              const priceCurrentEl = item.querySelector('.m-shop-the-look__item-price-current');
              if (priceCurrentEl && matchingVariant.price_formatted) {
                priceCurrentEl.textContent = matchingVariant.price_formatted;
              }

              const priceCompareEl = item.querySelector('.m-shop-the-look__item-price--compare');
              if (priceCompareEl) {
                if (matchingVariant.compare_at_price > matchingVariant.price && matchingVariant.compare_at_price_formatted) {
                  priceCompareEl.textContent = matchingVariant.compare_at_price_formatted;
                  priceCompareEl.style.display = 'inline';
                } else {
                  priceCompareEl.style.display = 'none';
                }
              }

              // Update thumbnail image if variant has a unique image
              if (matchingVariant.featured_image) {
                const thumbImg = item.querySelector('[data-item-thumb]');
                if (thumbImg) {
                  thumbImg.src = matchingVariant.featured_image;
                }
              }

              // Update single ATC button state
              const singleAtcBtn = item.querySelector('.m-shop-the-look__single-atc-btn');
              const btnTextEl = item.querySelector('.m-shop-the-look__btn-text');
              if (singleAtcBtn && btnTextEl) {
                if (matchingVariant.available) {
                  singleAtcBtn.disabled = false;
                  btnTextEl.textContent = 'Add item';
                } else {
                  singleAtcBtn.disabled = true;
                  btnTextEl.textContent = 'Sold out';
                }
              }

              // Refresh bundle total calculation
              this.updateBundleSummary();
            }
          });
        });
      });
    }

    updateBundleSummary() {
      const items = this.querySelectorAll('.m-shop-the-look__item');
      let totalPrice = 0;
      let totalComparePrice = 0;
      let allAvailable = true;

      items.forEach((item) => {
        const price = parseInt(item.dataset.variantPrice, 10) || 0;
        const comparePrice = parseInt(item.dataset.variantComparePrice, 10) || price;
        const isAvailable = item.dataset.variantAvailable !== 'false';

        totalPrice += price;
        totalComparePrice += comparePrice > price ? comparePrice : price;
        if (!isAvailable) {
          allAvailable = false;
        }
      });

      const totalEl = this.querySelector('[data-bundle-total]');
      const compareEl = this.querySelector('[data-bundle-compare]');
      const savingsEl = this.querySelector('[data-bundle-savings]');
      const bundleBtn = this.querySelector('[data-bundle-add]');

      if (totalEl) {
        totalEl.textContent = this.formatCurrency(totalPrice);
      }

      if (compareEl && savingsEl) {
        if (totalComparePrice > totalPrice) {
          const savings = totalComparePrice - totalPrice;
          compareEl.textContent = this.formatCurrency(totalComparePrice);
          compareEl.style.display = 'inline';
          savingsEl.textContent = `Save ${this.formatCurrency(savings)}`;
          savingsEl.style.display = 'inline';
        } else {
          compareEl.style.display = 'none';
          savingsEl.style.display = 'none';
        }
      }

      if (bundleBtn) {
        bundleBtn.disabled = !allAvailable;
      }
    }

    formatCurrency(cents) {
      if (typeof window.formatMoney === 'function') {
        return window.formatMoney(cents, window.MinimogSettings?.money_format || '${{amount}}');
      }
      if (window.MinimogTheme && typeof window.MinimogTheme.formatMoney === 'function') {
        return window.MinimogTheme.formatMoney(cents, window.MinimogSettings?.money_format || '${{amount}}');
      }
      return '$' + (cents / 100).toFixed(2);
    }

    openCartDrawer() {
      const cartDrawer = document.querySelector('m-cart-drawer') || document.getElementById('MinimogCartDrawer');
      if (cartDrawer) {
        if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        } else {
          cartDrawer.classList.add('m-cart-drawer--active');
          document.documentElement.classList.add('prevent-scroll');
          cartDrawer.style.setProperty('--m-bg-opacity', '0.5');
          cartDrawer.style.setProperty('--translate-x', '0');
        }
      }

      // Emit Minimog Theme pubSub event for openCartDrawer
      if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.openCartDrawer) {
        window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.openCartDrawer);
      }

      // Header cart button trigger fallback
      const headerCartTrigger = document.querySelector('.m-cart-icon-bubble, [data-open-cart-drawer]');
      if (headerCartTrigger && (!cartDrawer || !cartDrawer.classList.contains('m-cart-drawer--active'))) {
        headerCartTrigger.click();
      }
    }

    initSingleAtc() {
      const buttons = this.querySelectorAll('.m-shop-the-look__single-atc-btn');
      buttons.forEach((btn) => {
        btn.addEventListener('click', async (e) => {
          e.preventDefault();
          const itemRow = btn.closest('.m-shop-the-look__item');
          if (!itemRow) return;

          const variantId = itemRow.dataset.variantId;
          if (!variantId) {
            // Preview demonstration in editor when unassigned
            this.openCartDrawer();
            return;
          }

          const textEl = btn.querySelector('.m-shop-the-look__btn-text');
          const originalText = textEl ? textEl.textContent : btn.textContent;

          btn.classList.add('is-loading');
          btn.disabled = true;
          if (textEl) textEl.textContent = 'Adding...';

          try {
            const res = await this.addToCart([{ id: parseInt(variantId, 10), quantity: 1 }]);
            btn.classList.remove('is-loading');

            if (res && !res.status) {
              if (textEl) textEl.textContent = 'Added ✓';
              setTimeout(() => {
                if (textEl) textEl.textContent = originalText;
                btn.disabled = false;
              }, 1600);
            } else {
              if (textEl) textEl.textContent = 'Error';
              setTimeout(() => {
                if (textEl) textEl.textContent = originalText;
                btn.disabled = false;
              }, 2000);
            }
          } catch (err) {
            console.error('Failed to add item to bag', err);
            btn.classList.remove('is-loading');
            if (textEl) textEl.textContent = originalText;
            btn.disabled = false;
          }
        });
      });
    }

    initBundleAtc() {
      const bundleBtn = this.querySelector('[data-bundle-add]');
      if (!bundleBtn) return;

      bundleBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        const items = this.querySelectorAll('.m-shop-the-look__item');
        const bundleItems = [];

        items.forEach((item) => {
          const variantId = item.dataset.variantId;
          if (variantId) {
            bundleItems.push({ id: parseInt(variantId, 10), quantity: 1 });
          }
        });

        if (bundleItems.length === 0) {
          // Preview demonstration
          this.openCartDrawer();
          return;
        }

        const textEl = bundleBtn.querySelector('.m-shop-the-look__bundle-btn-text');
        const originalText = textEl ? textEl.textContent : bundleBtn.textContent;

        bundleBtn.classList.add('is-loading');
        bundleBtn.disabled = true;
        if (textEl) textEl.textContent = 'Adding look to bag...';

        try {
          const res = await this.addToCart(bundleItems);
          bundleBtn.classList.remove('is-loading');

          if (res && !res.status) {
            if (textEl) textEl.textContent = 'Look Added to Bag ✓';
            setTimeout(() => {
              if (textEl) textEl.textContent = originalText;
              bundleBtn.disabled = false;
            }, 2500);
          } else {
            if (textEl) textEl.textContent = 'Error adding items';
            setTimeout(() => {
              if (textEl) textEl.textContent = originalText;
              bundleBtn.disabled = false;
            }, 2500);
          }
        } catch (err) {
          console.error('Failed to add bundle to bag', err);
          bundleBtn.classList.remove('is-loading');
          if (textEl) textEl.textContent = originalText;
          bundleBtn.disabled = false;
        }
      });
    }

    async addToCart(items) {
      const cartAddUrl = window.MinimogSettings?.routes?.cart_add_url || '/cart/add.js';
      const cartDrawer = document.querySelector('m-cart-drawer') || document.getElementById('MinimogCartDrawer');

      // Request sections to refresh drawer and count immediately
      let sectionsToBundle = ['cart-drawer', 'cart-count'];
      try {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );
      } catch (e) {}

      const postData = {
        items: items,
        sections: sectionsToBundle,
        sections_url: window.location.pathname
      };

      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(postData)
      });

      const data = await response.json();

      if (!data.status) {
        // 1. Emit pubSub cart update event
        if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
          window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, { cart: data });
        }

        // 2. Dispatch cart:refresh with open: true for full drawer and counter re-render
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));

        // 3. Dispatch native product-ajax:added event
        document.dispatchEvent(
          new CustomEvent('product-ajax:added', {
            detail: { product: data }
          })
        );

        // 4. Notification feedback inside drawer if supported
        if (window.MinimogTheme?.Notification && cartDrawer) {
          const itemsContainer = cartDrawer.querySelector('m-cart-items');
          if (itemsContainer) {
            window.MinimogTheme.Notification.show({
              target: itemsContainer,
              method: 'prepend',
              type: 'success',
              message: window.MinimogStrings?.itemAdded || 'Item added to bag',
              delay: 400
            });
          }
        }

        // 5. Open the cart drawer immediately
        this.openCartDrawer();

        // 6. Ensure cart drawer remains open after async DOM refresh
        setTimeout(() => {
          this.openCartDrawer();
        }, 150);

        setTimeout(() => {
          this.openCartDrawer();
        }, 400);
      }

      return data;
    }
  }

  customElements.define('m-shop-the-look', MShopTheLook);
}
